import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { useCredentialDialog } from '../../hooks/useCredentialDialog';
import { getPeopleCredentials, deletePersonCredential } from '../../services/credentialService';
import { CREDENTIAL_GRID_COLUMNS } from '../../constants/personCredentialFormConstants';

const PersonCredentialsTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const { openCredentialDialog } = useCredentialDialog();
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadCredentials = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleCredentials(personId),
      onSuccess: (data) => setCredentials(data),
    });
  }, [dispatch, personId]);

  const handleAdd = () => openCredentialDialog({ personId, onSaved: loadCredentials });

  const handleEdit = (row) =>
    openCredentialDialog({ credentialId: row.ptsCredentialID, onSaved: loadCredentials });

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${row.Type || 'credential'}" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deletePersonCredential(row.ptsCredentialID),
            onSuccess: () => {
              dispatch(notify('Credential deleted successfully', 'success'));
              loadCredentials();
            },
          });
        },
        'Delete Credential'
      );
    },
    [dispatch, requestConfirm, loadCredentials]
  );

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Credential"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={credentials}
        columns={CREDENTIAL_GRID_COLUMNS}
        getRowId={(row) => row.ptsCredentialID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Credentials' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonCredentialsTab;
